/**
 * errorReasonVocabulary — the one shared vocabulary for `technical_trace.error`, the field that says
 * why a turn the employee could not carry out stopped where it did.
 *
 * Two surfaces print this field to the same technician, both under the row label "שגיאה": the
 * `ConversationPanel` trace box (mounted from the conversation page, the ticket's `ai-chat` tab and
 * the employee workspace) and the Handoff Center's evidence drawer. Both rendered the bare value, so
 * the producer's own identifier reached the operator inside an otherwise Hebrew row — the failure
 * `statusVocabulary`, `evidenceSourceVocabulary`, `toolNameVocabulary`, `actionTypeVocabulary` and
 * `handoffSourceVocabulary` exist to prevent, on the row directly below the one §3.72 fixed.
 *
 * This field is NOT a closed classification, and that is the decision it needed rather than the
 * sweep the four vocabularies above could run. `conversation_service.py` writes it from two kinds of
 * site. Six sites write a machine token: the five decline reasons `_decline` is called with
 * ("tool_not_registered" / "no_eset_connection" / "device_not_specified" / "not_available" /
 * "no_ticket_bound") and the manager's "no_matching_employee". Two write prose: the authorization
 * failure stores `decision.reason` — the policy engine's own sentence — and a failed run stores its
 * `result_summary`, truncated to 200 characters. So the field is a closed token set with a prose
 * escape hatch, and the accessor below is shaped for exactly that: a recognised token is named, and
 * anything else is returned verbatim, because a sentence in this field is evidence and not a token
 * this build forgot to map.
 *
 * Which is why the fallback is the token itself (the choice `traceToolLabel` and `actionTypeLabel`
 * make) and not the "unattributed" word `handoffSourceLabel` uses. That field's producer set is
 * closed at two and its second reader already answers an unrecognised source with `unknown`; this
 * one is open by design, so replacing prose with a shrug would delete the operator's evidence and
 * hide the drift the parity half of `errorReasonVocabulary.test.jsx` exists to catch.
 *
 * `_decline` is a forwarding wrapper: it takes the reason as its fifth argument and passes it to
 * `_build_technical_trace(error=error)`, so five of the six tokens never appear beside `error=` in
 * the source. The sweep resolves that wrapper in the same file rather than reading the literals at
 * the builder's call sites, and asserts the forwarding itself — the hole §3.69 recorded for the
 * action map, closed here for this field.
 */
export const TRACE_ERROR_LABEL = {
  // The tool registry has no entry for what the technician asked for. The human reply beside this
  // row already says the capability is not there; the label names the registry lookup, which is the
  // technical fact the row exists to carry.
  tool_not_registered: "אין יכולת רשומה כזו במערכת",
  // The employee has no ESET connection configured, so the read could not be attempted at all.
  no_eset_connection: "אין חיבור ESET לעובד",
  // The request named no device and the conversation carries none either.
  device_not_specified: "לא צוין מכשיר",
  // The capability is beyond this build (the Acronis read, reached only where the connector exists).
  not_available: "היכולת אינה זמינה בגרסה זו",
  // A ticket-scoped request on a conversation that was not opened from a ticket.
  no_ticket_bound: "השיחה אינה מקושרת לקריאה",
  // The manager's router found nobody to hand the request to.
  no_matching_employee: "לא נמצא עובד מתאים לפנייה",
};

/**
 * The Hebrew name of one trace-error token, or the value itself when it is not one of the tokens
 * this build names.
 *
 * The pass-through is deliberate and is pinned from both sides: the six tokens must come out as
 * words, and a policy sentence or a run summary must come out unchanged. A future producer token
 * that is not in the map prints raw on both surfaces rather than silently, and the parity pin in the
 * test reddens instead — a new token fails loudly where the *set* is read, which is the only place
 * the set can be read.
 */
export function traceErrorLabel(value) {
  if (typeof value !== "string" || !value) return null;
  return TRACE_ERROR_LABEL[value] ?? value;
}
